import { useState } from "react";
import styled from "styled-components";
import formatCLP from "../../utils/formatCLP";
import { MEDIOS_DE_PAGO } from "./EntradasYPago";

// Pago de toda la venta, al final del flujo del vendedor (17/10/2026), antes de descargar el voucher.
// Uno o varios medios, cada uno con su porcentaje o su monto (ej. 10% Wise + 10% Link de Pago). El
// backend reparte cada medio entre las reservas según lo que debe cada una.

export type LineaPago = { medio: string, monto: number }

export const montoDePorcentaje = (total: number, pct: number) => Math.round(total * (Number(pct) || 0) / 100)
export const porcentajeDe = (total: number, monto: number) => total > 0 ? Math.round(monto / total * 1000) / 10 : 0

// Lo que falta para poder registrar el pago, o null.
export function faltaPago(lineas: LineaPago[], total: number): string | null {
    if (lineas.some(l => !l.medio)) return 'Elige el medio de pago de cada línea.'
    if (lineas.some(l => !(l.monto > 0))) return 'Escribe el porcentaje o el monto de cada línea.'
    const medios = lineas.map(l => l.medio)
    if (new Set(medios).size !== medios.length) return 'Hay un medio de pago repetido: júntalo en una sola línea.'
    const pagado = lineas.reduce((s, l) => s + l.monto, 0)
    if (pagado > total + 1) return `Lo pagado (${formatCLP(pagado)}) es más que el total de la venta (${formatCLP(total)}).`
    return null
}

export default function PagoVenta({ total, inicial, onRegistrar, onVolver }: {
    total: number,
    inicial?: LineaPago[],
    onRegistrar: (lineas: LineaPago[]) => void,
    onVolver: () => void,
}) {
    const [lineas, setLineas] = useState<LineaPago[]>(inicial?.length ? inicial : [{ medio: '', monto: 0 }])
    const pagado = lineas.reduce((s, l) => s + (l.monto || 0), 0)
    const falta = faltaPago(lineas, total)

    const cambiar = (i: number, cambio: Partial<LineaPago>) => setLineas(prev => prev.map((l, j) => j === i ? { ...l, ...cambio } : l))
    // Atajos: lo que pagó en total, en la primera línea (las demás se quitan).
    // 0% = no pagó nada: sin líneas.
    const atajo = (pct: number) => setLineas(prev => pct === 0 ? [] : [{ medio: prev[0]?.medio || '', monto: montoDePorcentaje(total, pct) }])
    // "Otro" (17/10/2026): cualquier porcentaje entero de 0 a 100.
    const [conOtro, setConOtro] = useState(false)
    const [otroPct, setOtroPct] = useState('')

    return (
        <Main>
            <h1>Pago</h1>
            <p className="total">Total de la venta: <strong>{formatCLP(total)}</strong></p>
            <div className="atajos" role="group" aria-label="Cuánto pagó">
                {[100, 80, 20].map(p => <button type="button" key={p} onClick={() => { setConOtro(false); atajo(p) }}>{p}%</button>)}
                <button type="button" className={conOtro ? 'activo' : ''} onClick={() => setConOtro(true)}>Otro</button>
                {
                    conOtro &&
                    <label className="otro">
                        <input type="number" min={0} max={100} step={1} inputMode="numeric" aria-label="Otro porcentaje" placeholder="0 a 100"
                        value={otroPct}
                        onChange={e => {
                            const texto = e.target.value.replace(/\D/g, '')
                            if (texto === '') { setOtroPct(''); return }
                            const pct = Math.min(Number(texto), 100)
                            setOtroPct(String(pct))
                            atajo(pct)
                        }} />%
                    </label>
                }
            </div>
            {lineas.map((l, i) => (
                <div className="linea" key={i}>
                    <select aria-label={`Medio de pago ${i + 1}`} value={l.medio} onChange={e => cambiar(i, { medio: e.target.value })}>
                        <option value="">Medio de pago</option>
                        {MEDIOS_DE_PAGO.filter(m => m !== 'Deuda').map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <label>
                        <input type="number" min={0} max={100} step="any" aria-label={`Porcentaje ${i + 1}`}
                            value={l.monto ? porcentajeDe(total, l.monto) : ''}
                            onChange={e => cambiar(i, { monto: montoDePorcentaje(total, Number(e.target.value)) })} />%
                    </label>
                    <label>
                        $<input type="number" min={0} aria-label={`Monto ${i + 1}`}
                            value={l.monto || ''}
                            onChange={e => cambiar(i, { monto: Math.round(Number(e.target.value)) || 0 })} />
                    </label>
                    <button type="button" className="quitar" aria-label={`Quitar línea ${i + 1}`}
                        onClick={() => setLineas(prev => prev.filter((_, j) => j !== i))}>✕</button>
                </div>
            ))}
            <button type="button" className="agregar" onClick={() => setLineas(prev => [...prev, { medio: '', monto: 0 }])}>+ Agregar otro medio de pago</button>
            <p className="nota">Pagado {formatCLP(pagado)} ({porcentajeDe(total, pagado)}%) · queda debiendo {formatCLP(Math.max(total - pagado, 0))}</p>
            {/* El aviso aparece recién cuando se empezó a llenar algo. */}
            {falta && lineas.some(l => l.medio || l.monto) && <p className="alerta" role="alert">{falta}</p>}
            <div className="actions">
                <button type="button" onClick={onVolver}>Volver</button>
                <button type="button" disabled={!!falta && lineas.length > 0}
                    onClick={() => { if (!falta || lineas.length === 0) onRegistrar(lineas) }}>Registrar pago</button>
            </div>
        </Main>
    )
}

const Main = styled.div`
    .total { margin: 8px 0 12px; }
    .atajos { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
    .atajos button, .agregar {
        border: 1px solid #c9c9c9; border-radius: 999px; padding: 6px 14px; background: #fff; cursor: pointer; font-size: 13px;
    }
    .agregar { border-style: dashed; margin: 4px 0 8px; }
    .atajos button.activo { background: #2c3e50; border-color: #2c3e50; color: #fff; }
    .atajos .otro { display: flex; align-items: center; gap: 4px; font-size: 14px; }
    .atajos .otro input { width: 80px; border: 1px solid #c9c9c9; border-radius: 8px; padding: 6px 8px; font-size: 14px; }
    .linea { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
    .linea select { flex: 1 1 160px; border: 1px solid #c9c9c9; border-radius: 8px; padding: 8px; font-size: 14px; }
    .linea label { display: flex; align-items: center; gap: 4px; font-size: 14px; }
    .linea input { border: 1px solid #c9c9c9; border-radius: 8px; padding: 8px; font-size: 14px; }
    .linea label:nth-of-type(1) input { width: 70px; }
    .linea label:nth-of-type(2) input { width: 110px; }
    .quitar { border: none; background: none; cursor: pointer; font-size: 14px; color: #777; }
    .nota { font-size: 13px; color: #555; margin: 6px 0; }
    .alerta { font-size: 13px; color: #b45309; margin: 6px 0; }
`
